import type { Editor } from '@tiptap/react'

import type { Discount } from '@/gen/cortado/v1/invoices_pb'
import { DiscountTypeEnum } from '@/gen/cortado/v1/settings_pb'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { RichTextEditor } from '@/components/shared/rich-text-editor'
import { DiscountSelector } from './discount-selector'
import { EnforcedTipInput } from './enforced-tip-input'
import { DatePicker } from '@/components/shared/date-picker'

/**
 * Base form values that are common to all financial documents (invoices and quotes).
 * This allows the shared component to work with both document types while maintaining type safety.
 */
export interface FinancialDocumentFormValues {
  // Core fields (used by both invoices and quotes)
  title: string
  discountId?: string
  enforcedTipPercentage?: number

  // Quote-specific fields (invoices will have these but ignore them)
  validUntil?: Date
  discountName?: string
  discountType?: DiscountTypeEnum
  discountValue?: number
  enforcedTipEnabled?: boolean

  // Additional fields that may exist on the form but aren't directly used in details
  // (keeping this flexible for extension)
  [key: string]: any
}

/**
 * Minimal typed interface for the TanStack Form object.
 * This extracts just what we need from FormApi without fighting its complex generics.
 */
export interface FinancialDocumentForm {
  Field: any // TanStack Form's Field component
  state: {
    values: FinancialDocumentFormValues
  }
  setFieldValue: (name: any, value: any) => void
}

interface FinancialDocumentDetailsFieldsProps {
  form: FinancialDocumentForm
  editor?: Editor | null
  discounts: Discount[]
  organizationId: string
  disabled?: boolean
  showValidUntil?: boolean
  organizationTipPercentage?: number
  hasEnforcedTip?: boolean
  supportsInlineDiscounts?: boolean
  /** Rendered under the enforced tip input (the tip collection mode control). */
  tipAllocationControl?: React.ReactNode
  richTextEditor?: React.ReactNode
  onFieldChange?: () => void
}

/**
 * FinancialDocumentDetailsFields - Reusable form fields for invoice/quote details
 *
 * Features:
 * - Title input
 * - Rich text description editor
 * - Valid Until date picker (optional, quote-only)
 * - Discount selector
 * - Enforced tip percentage input
 */
export function FinancialDocumentDetailsFields({
  form,
  editor,
  discounts,
  organizationId,
  disabled = false,
  showValidUntil = false,
  organizationTipPercentage,
  hasEnforcedTip = false,
  tipAllocationControl,
  supportsInlineDiscounts = false,
  richTextEditor,
  onFieldChange,
}: FinancialDocumentDetailsFieldsProps) {
  return (
    <div className="space-y-4">
      {/* Title */}
      <form.Field name="title">
        {(field: { state: { value: string }; handleChange: (value: string) => void }) => (
          <div className="grid gap-3">
            <Label htmlFor="title">
              Title
              {showValidUntil && <span className="text-destructive"> *</span>}
            </Label>
            <Input
              id="title"
              name="title"
              autoComplete="off"
              value={field.state.value}
              disabled={disabled}
              onChange={(event) => {
                field.handleChange(event.target.value)
                onFieldChange?.()
              }}
              placeholder={showValidUntil ? 'Enter quote title…' : 'Invoice Title'}
              required={showValidUntil}
            />
          </div>
        )}
      </form.Field>

      {/* Description */}
      <div className="grid">
        <Label className="mb-3">Description</Label>
        {richTextEditor || (
          <RichTextEditor
            editor={editor ?? null}
            disabled={disabled}
            minHeight={showValidUntil ? '100px' : '120px'}
          />
        )}
      </div>

      {/* Valid Until (Quote only) */}
      {showValidUntil && (
        <form.Field name="validUntil">
          {(field: { state: { value: Date | undefined }; handleChange: (value: Date) => void }) => (
            <div className="grid gap-3">
              <Label htmlFor="valid-until">Valid Until</Label>
              <DatePicker
                value={field.state.value}
                onChange={(date) => {
                  field.handleChange(date || new Date())
                  onFieldChange?.()
                }}
                disabled={disabled}
              />
            </div>
          )}
        </form.Field>
      )}

      {/* Discount and Enforced Tip - Side by side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Discount */}
        <form.Field name="discountId">
          {(field: { state: { value: string | undefined }; handleChange: (value: string | undefined) => void }) => {
            // Create inlineDiscount ONLY when there's ACTUAL discount data to show
            // Requirements: no discountId, AND has a name, AND has a positive value
            // This prevents creating "phantom" inline discounts from fallback values
            const hasActualDiscountData =
              form.state.values.discountName &&
              form.state.values.discountValue !== undefined &&
              form.state.values.discountValue > 0

            // Check for no discountId (undefined or empty string both count as "no discount")
            const hasNoDiscountId = !field.state.value || field.state.value === ''
            const isInlineDiscount = hasNoDiscountId && hasActualDiscountData

            const inlineDiscount = supportsInlineDiscounts && isInlineDiscount
              ? {
                // Type assertions are safe here because hasActualDiscountData ensures these exist
                name: form.state.values.discountName as string,
                type: form.state.values.discountType || DiscountTypeEnum.DISCOUNT_TYPE_PERCENTAGE,
                value: form.state.values.discountValue as number,
              }
              : undefined

            return (
              <DiscountSelector
                value={field.state.value}
                discounts={discounts}
                organizationId={organizationId}
                inlineDiscount={inlineDiscount}
                onChange={(discountId) => {
                  // Use empty string instead of undefined for clearing
                  // TanStack Form may not properly handle undefined for field clearing
                  field.handleChange(discountId === undefined ? '' : discountId)
                  onFieldChange?.()
                }}
                onInlineDiscountChange={(discount) => {
                  if (supportsInlineDiscounts) {
                    form.setFieldValue('discountName', discount?.name)
                    form.setFieldValue('discountType', discount?.type)
                    form.setFieldValue('discountValue', discount?.value)
                    onFieldChange?.()
                  }
                }}
                disabled={disabled}
                supportsInlineDiscounts={supportsInlineDiscounts}
              />
            )
          }}
        </form.Field>

        {/* Enforced Tip */}
        <form.Field name="enforcedTipPercentage">
          {(field: { state: { value: number | undefined }; handleChange: (value: number) => void }) => (
            <EnforcedTipInput
              value={field.state.value || 0}
              onChange={(value) => {
                field.handleChange(value)
                // For quotes, also update enforcedTipEnabled
                if (form.state.values.enforcedTipEnabled !== undefined) {
                  form.setFieldValue('enforcedTipEnabled', value > 0)
                }
                onFieldChange?.()
              }}
              disabled={disabled}
              organizationDefault={organizationTipPercentage}
              showOrganizationHint={hasEnforcedTip}
            />
          )}
        </form.Field>

        {tipAllocationControl}
      </div>
    </div>
  )
}
